import {useEffect, useState} from 'react';
import {Button, Text, Progress, useDisclosure} from '@chakra-ui/react';
import {useSession} from '../contexts/SessionContext';
import {useAuth} from '../contexts/AuthContext';
import ModalFrame from './common/ModalFrame';

const SessionTimeoutWarning = () => {
  const {getRemainingTime, resetTimer, getWarningTime, secureLogout} =
    useSession();
  const {logout} = useAuth();
  const {isOpen, onOpen, onClose} = useDisclosure();
  const [timeLeft, setTimeLeft] = useState(0);
  const [intervalId, setIntervalId] = useState<any>(null);
  const warningTime = getWarningTime();

  useEffect(() => {
    const checkSessionTimeout = () => {
      const remainingTime = getRemainingTime();

      // If remaining time is less than warning time, show the modal
      if (remainingTime <= warningTime && !isOpen) {
        setTimeLeft(remainingTime);
        onOpen();

        // Set up an interval to update the countdown timer
        const id = setInterval(() => {
          setTimeLeft(prevTime => {
            const newTime = getRemainingTime();

            // If time is up, logout securely
            if (newTime <= 0) {
              clearInterval(id);
              console.debug('logging out - session timeout warning expired');
              secureLogout ? secureLogout() : logout();
              return 0;
            }

            return newTime;
          });
        }, 1000);

        setIntervalId(id);
      }
    };

    // Check every 10 seconds
    const id = setInterval(checkSessionTimeout, 10000);

    return () => {
      clearInterval(id);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [
    getRemainingTime,
    isOpen,
    onOpen,
    logout,
    secureLogout,
    intervalId,
    warningTime,
  ]);

  // When user chooses to stay logged in
  const handleStayLoggedIn = () => {
    resetTimer();
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
    onClose();
  };

  // When user chooses to logout
  const handleLogout = () => {
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
    console.debug('logging out - user selected logout from timeout warning');
    secureLogout ? secureLogout() : logout();
  };

  // Format time left in minutes and seconds
  const formatTimeLeft = () => {
    const minutes = Math.floor(timeLeft / 60000);
    const seconds = Math.floor((timeLeft % 60000) / 1000);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  // Calculate progress bar value (from 100 to 0)
  const progressValue = (timeLeft / warningTime) * 100;
  const footer = (
    <>
      <Button colorScheme="gray" mr={3} onClick={handleStayLoggedIn}>
        Stay Logged In
      </Button>
      <Button variant="outline" onClick={handleLogout}>
        Logout
      </Button>
    </>
  );

  return (
    <ModalFrame
      isOpen={isOpen}
      onClose={handleStayLoggedIn}
      closeOnOverlayClick={false}
      title="Session Timeout Warning"
      showCloseButton={false}
      footer={footer}
    >
      <Text mb={4}>
        Your session is about to expire due to inactivity. You will be
        automatically logged out in:
      </Text>
      <Text textAlign="center" fontSize="2xl" fontWeight="normal" mb={2}>
        {formatTimeLeft()}
      </Text>
      <Progress value={progressValue} colorScheme="gray" size="sm" mb={4} />
      <Text fontSize="sm" color="gray.600" mb={4}>
        For security purposes, this application automatically logs out users
        after periods of inactivity and when browser tabs are closed.
      </Text>
      <Text>Would you like to continue your session or log out now?</Text>
    </ModalFrame>
  );
};

export default SessionTimeoutWarning;
